/**
 * useExecutionPolling - Hook for polling execution status in real-time
 *
 * Polls an execution's status while it's running and automatically stops
 * when the execution completes. Uses React Query's refetchInterval feature
 * with dynamic enabling based on execution status.
 *
 * Handles the case where the execution record doesn't exist yet (404) by
 * continuing to poll during an initial grace period while the Celery task
 * creates the execution record.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type { ConnectorExecution, ExecutionStatus } from '@/types/connectors';

const API_BASE = '/connectors';

/**
 * Grace period in milliseconds during which we tolerate 404 errors.
 * This allows time for the Celery task to create the execution record.
 */
const EXECUTION_NOT_FOUND_GRACE_PERIOD_MS = 15000; // 15 seconds

/**
 * Fetch execution details (same as useExecutionDetails but for polling)
 */
async function fetchExecutionForPolling(
  connectorType: string,
  executionId: string
): Promise<ConnectorExecution> {
  const response = await apiClient.get(
    `${API_BASE}/${connectorType}/executions/${executionId}`
  );
  // Endpoint returns ``{ execution: {...} }``; list endpoints return flat
  // execution objects. Unwrap so callers always see a flat shape.
  const data = response.data;
  if (data && typeof data === 'object' && 'execution' in data) {
    return (data as { execution: ConnectorExecution }).execution;
  }
  return data as ConnectorExecution;
}

/**
 * Check if execution is still in progress
 */
function isExecutionInProgress(status: ExecutionStatus): boolean {
  // 'cancelling' (#1046) counts as in-progress: the revoke was issued but the
  // worker never confirmed it stopped, so the run may still be writing. Keep
  // polling until the liveness reconcile promotes it to 'cancelled'.
  return status === 'pending' || status === 'running' || status === 'cancelling';
}

export interface UseExecutionPollingOptions {
  pollInterval?: number; // milliseconds, default 3000 (3 seconds)
  enabled?: boolean; // allow manual control
  onStatusChange?: (status: ExecutionStatus, execution: ConnectorExecution) => void;
  onComplete?: (execution: ConnectorExecution) => void;
  onError?: (execution: ConnectorExecution) => void;
  /** Grace period in ms to tolerate 404 errors while execution record is being created */
  gracePeriodMs?: number;
}

export interface UseExecutionPollingResult {
  execution: ConnectorExecution | undefined;
  isPolling: boolean;
  /** True when waiting for execution record to be created (404 within grace period) */
  isWaitingForExecution: boolean;
  error: Error | null;
  stopPolling: () => void;
}

/**
 * Hook to poll execution status while it's running
 *
 * @param executionId - The execution ID to poll (null to disable)
 * @param connectorType - The connector type
 * @param options - Polling options
 *
 * @example
 * const { execution, isPolling } = useExecutionPolling(
 *   executionId,
 *   'google_ads',
 *   {
 *     pollInterval: 3000,
 *     onComplete: (exec) => showSuccessToast(exec),
 *     onError: (exec) => showErrorToast(exec),
 *   }
 * );
 */
export function useExecutionPolling(
  executionId: string | null,
  connectorType: string,
  options: UseExecutionPollingOptions = {}
): UseExecutionPollingResult {
  const {
    pollInterval = 3000,
    enabled = true,
    onStatusChange,
    onComplete,
    onError,
    gracePeriodMs = EXECUTION_NOT_FOUND_GRACE_PERIOD_MS,
  } = options;

  const queryClient = useQueryClient();
  const previousStatusRef = React.useRef<ExecutionStatus | null>(null);

  // Track when polling started for this execution to implement grace period.
  // Kept as state (not a ref) so `isWithinGracePeriod` can be read during
  // render without violating the refs-during-render rule.
  const [pollingStartTime, setPollingStartTime] = React.useState<number | null>(null);

  // Reset start time when executionId changes. `Date.now()` is impure, so it
  // can't be called during render (react-hooks/purity) -- deferring the
  // setState to a microtask keeps it out of the effect body's synchronous
  // path too (react-hooks/set-state-in-effect), matching the pattern used
  // elsewhere in this codebase (see ColumnConfigStep's rowSelection sync).
  React.useEffect(() => {
    if (executionId) {
      queueMicrotask(() => setPollingStartTime(Date.now()));
    } else {
      queueMicrotask(() => setPollingStartTime(null));
    }
  }, [executionId]);

  /**
   * Check if we're still within the grace period where 404 errors are tolerated.
   * During this period, the Celery task may not have created the execution record yet.
   */
  const isWithinGracePeriod = React.useCallback(() => {
    if (!pollingStartTime) return false;
    const elapsed = Date.now() - pollingStartTime;
    return elapsed < gracePeriodMs;
  }, [gracePeriodMs, pollingStartTime]);

  const query = useQuery({
    queryKey: ['executionPolling', connectorType, executionId],
    queryFn: () => fetchExecutionForPolling(connectorType, executionId!),
    enabled: enabled && !!executionId && !!connectorType,
    refetchInterval: (query) => {
      const error = query.state.error as any;

      // Handle 404 errors specially based on grace period
      if (error?.response?.status === 404) {
        // During grace period, continue polling - execution record may not exist yet
        if (isWithinGracePeriod()) {
          return pollInterval;
        }
        // After grace period, stop polling - execution truly doesn't exist
        return false;
      }

      // Stop polling on other errors
      if (error) return false;

      const execution = query.state.data as ConnectorExecution | undefined;
      // If no data yet but no error, continue polling (initial request)
      if (!execution) return pollInterval;
      // Stop polling when execution is complete
      if (!isExecutionInProgress(execution.status)) return false;

      return pollInterval;
    },
    // Keep previous data while refetching for smooth UI
    placeholderData: (previousData) => previousData,
    // Don't cache polling data for long
    staleTime: 0,
    // Retry logic with grace period awareness for 404 errors
    retry: (failureCount, error: any) => {
      // For 404 errors, retry during grace period
      if (error?.response?.status === 404) {
        // During grace period, retry up to many times (polling will handle the rest)
        if (isWithinGracePeriod()) {
          return failureCount < 5;
        }
        // After grace period, don't retry 404 - execution truly doesn't exist
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (_attemptIndex, error: any) => {
      // For 404 during grace period, use the poll interval as retry delay
      if (error?.response?.status === 404 && isWithinGracePeriod()) {
        return pollInterval;
      }
      // Default retry delay for other errors
      return 1000;
    },
  });

  // Handle status changes using useEffect
  React.useEffect(() => {
    const execution = query.data;
    if (!execution) return;

    // Call status change callback
    if (onStatusChange && execution.status !== previousStatusRef.current) {
      onStatusChange(execution.status, execution);
      previousStatusRef.current = execution.status;
    }

    // If execution just completed, call appropriate callback
    if (!isExecutionInProgress(execution.status)) {
      if (execution.status === 'failed') {
        onError?.(execution);
      } else {
        // All other terminal statuses (success, partial, cancelled, timeout,
        // skipped) go through onComplete. Consumers can branch on
        // execution.status if they need different UX per status (#123).
        if (execution.status === 'success' || execution.status === 'partial') {
          // Successful/partial runs land new rows. Invalidate the list queries.
          queryClient.invalidateQueries({ queryKey: ['datasets'] });
          queryClient.invalidateQueries({ queryKey: ['schemas'] });
        }
        onComplete?.(execution);
      }
    }
  }, [query.data, onStatusChange, onComplete, onError, queryClient]);

  // Check if we're in a 404 grace period state
  const is404Error = (query.error as any)?.response?.status === 404;
  const isWaitingForExecution = is404Error && isWithinGracePeriod();

  // isPolling is true when:
  // 1. Initial loading
  // 2. We have data and execution is in progress
  // 3. We're waiting for the execution record to be created (404 within grace period)
  const isPolling = query.isLoading ||
    isWaitingForExecution ||
    (!!query.data && isExecutionInProgress(query.data.status) && query.isFetching);

  // Only expose the error if we're past the grace period for 404s
  const effectiveError = is404Error && isWithinGracePeriod()
    ? null  // Hide 404 errors during grace period
    : (query.error as Error | null);

  return {
    execution: query.data,
    isPolling,
    isWaitingForExecution,
    error: effectiveError,
    stopPolling: () => {
      queryClient.removeQueries({ queryKey: ['executionPolling', connectorType, executionId] });
    },
  };
}

/**
 * Calculate elapsed time in seconds
 */
export function useElapsedTime(startedAt?: string): number {
  // `now` is only ever updated while `startedAt` is set; when it's absent we
  // derive 0 directly in render below instead of resetting state from the
  // effect (avoids a setState-in-effect cascading render).
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!startedAt) return;

    // Update immediately, but deferred to a microtask rather than a
    // synchronous setState call in the effect body (avoids cascading
    // renders); mirrors the pattern used elsewhere in this codebase (see
    // ColumnConfigStep's rowSelection sync).
    queueMicrotask(() => setNow(Date.now()));

    // Update every second
    const interval = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(interval);
  }, [startedAt]);

  if (!startedAt) return 0;

  const start = new Date(startedAt).getTime();
  return Math.floor((now - start) / 1000);
}
